import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { Avatar } from "antd";
import axios from "axios";
import { ArrowRight, Eye, EyeOff, Save, Upload } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Textarea } from "src/components/ui/textarea";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../lib/api";
import { Skeleton } from "src/components/ui/skeleton";
import UploadImage from "../components/Common/UploadImage";

export default function EditUser() {
  const navigate = useNavigate();
  const { bid } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    work_place: "",
    work_days: "",
    work_hours: "",
    salary: "",
    role: "",
    address: "",
    phone: "",
    qualification: "",
    category_id: "",
    image: null,
  });
  const [showPassword, setShowPassword] = useState(false);

  // User
  const [loadingUser, setLoadingUser] = useState(false);
  const handleFetchUser = async () => {
    try {
      setLoadingUser(true);
      const { data } = await axios.get(`${BaseUrlApi}/users/${bid}`);

      setFormData({
        name: data.data?.name,
        email: data.data?.email,
        work_place: data.data?.work_place,
        work_days: data.data?.work_days,
        work_hours: Number.parseInt(data.data?.work_hours),
        salary: data.data?.salary,
        role: data.data?.role,
        address: data.data?.address,
        phone: data.data?.phone,
        qualification: data.data?.qualification,
        category_id: data.data?.category && data.data?.category[0]?.id,
        image: data.data?.image,
      });
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingUser(false);
    }
  };

  // Categories
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [Categories, setCategories] = useState([]);
  const handleFetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const { data } = await axios.get(`${BaseUrlApi}/categories`);
      setCategories(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    handleFetchUser();
    handleFetchCategories();
  }, []);

  const [isLoading, setIsLoading] = useState(false);

  // Track selected
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: Number.parseInt(value) || 0 }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      await axios.put(`${BaseUrlApi}/users/${bid}`, formData);
      toast("تم التعديل", { description: "تم تحديث تفاصيل المستخدم بنجاح ✏️" });

      navigate(-1);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
      setErrors(result);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loadingCategories || loadingUser) {
    return (
      <div className="animate-pulse" dir="rtl">
        {/* Header Skeleton */}
        <div className="mb-6 flex items-center">
          <Skeleton className="ml-2 h-8 w-48" />
        </div>

        <form>
          <div className="grid gap-6 md:grid-cols-2">
            {/* Personal Information Skeleton */}
            <div className="border rounded overflow-hidden flex flex-col h-full shadow">
              <div className="bg-gradient-to-r from-secondaryColor/20 to-transparent p-4">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-60 mt-1" />
              </div>
              <div className="p-6 space-y-4 flex flex-col flex-1">
                <div className="flex justify-center mb-6 relative">
                  <Skeleton className="h-24 w-24 rounded-full border-4 border-background" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-8 w-full" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-full" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-full" />
                </div>

                <div className="flex flex-col space-y-2 flex-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-16 w-full" />
                </div>
              </div>
            </div>

            {/* Work Information Skeleton */}
            <div className="border rounded overflow-hidden shadow">
              <div className="bg-gradient-to-r from-secondaryColor/20 to-transparent p-4">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-60 mt-1" />
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-8 w-full" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-8 w-full" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-8 w-full" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-8 w-full" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-8 w-full" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-8 w-full" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-8 w-full" />
                </div>
              </div>
            </div>
          </div>

          {/* Form Footer Skeleton */}
          <div className="flex justify-between mt-6">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
        </form>
      </div>
    );
  }

  console.log(formData.work_place);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", duration: 2 }}
      dir="rtl"
    >
      <div className="mb-6 flex items-center">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCancel}
          className="ml-2"
        >
          <ArrowRight className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">
          تعديل بيانات المستخدم {formData?.name}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 md:grid-cols-2">
          {/* Personal Information */}
          <Card className="overflow-hidden flex flex-col h-full">
            <CardHeader className="bg-gradient-to-r from-secondaryColor/20 to-transparent">
              <CardTitle className="text-xl">المعلومات الشخصية</CardTitle>
              <CardDescription>
                تعديل المعلومات الأساسية للمستخدم
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4 flex flex-col flex-1">
              <div className="flex justify-center mb-6">
                <div className="flex justify-center mb-6">
                  <UploadImage
                    name="users"
                    disabled={false}
                    imageUrl={formData.image && `${UrlImage}${formData.image}`}
                    onImageUploaded={(url) =>
                      setFormData({ ...formData, image: url })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="name">الاسم</label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="focus-visible:ring-mainColor"
                />
                {errors?.name && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.name[0]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="email">البريد الإلكتروني</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="focus-visible:ring-mainColor"
                />
                {errors?.email && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.email[0]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password">كلمة المرور</label>

                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    placeholder="أدخل كلمة المرور"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    className="focus-visible:ring-mainColor"
                  />

                  <div
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowPassword(!showPassword);
                    }}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </div>
                </div>
                {errors?.password && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.password[0]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="phone">رقم الهاتف</label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="focus-visible:ring-mainColor"
                />
                {errors?.phone && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.phone[0]}
                  </p>
                )}
              </div>

              <div className="flex flex-col space-y-2 flex-1">
                <label htmlFor="address">العنوان</label>
                <Textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="focus-visible:ring-mainColor flex-1"
                />
                {errors?.address && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.address[0]}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Work Information */}
          <Card className="overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-secondaryColor/20 to-transparent">
              <CardTitle className="text-xl">معلومات العمل</CardTitle>
              <CardDescription>تعديل معلومات العمل والمؤهلات</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <label htmlFor="work_place">مكان العمل</label>
                <Select
                  value={formData.work_place}
                  onValueChange={(value) =>
                    handleSelectChange("work_place", value)
                  }
                >
                  <SelectTrigger className="focus:ring-mainColor">
                    <SelectValue placeholder="اختر مكان العمل" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="remote">عن بعد</SelectItem>
                    <SelectItem value="onsite">في المكتب</SelectItem>
                  </SelectContent>
                </Select>
                {errors?.work_place && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.work_place[0]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="work_days">أيام العمل في الأسبوع</label>
                <Input
                  id="work_days"
                  name="work_days"
                  type="number"
                  min="1"
                  max="7"
                  value={formData.work_days}
                  onChange={handleNumberInputChange}
                  className="focus-visible:ring-mainColor"
                />
                {errors?.work_days && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.work_days[0]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="work_hours">ساعات العمل في اليوم</label>
                <Input
                  id="work_hours"
                  name="work_hours"
                  type="number"
                  min="1"
                  max="24"
                  value={formData.work_hours}
                  onChange={handleNumberInputChange}
                  className="focus-visible:ring-mainColor"
                />
                {errors?.work_hours && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.work_hours[0]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="qualification">المؤهل العلمي</label>
                <Input
                  id="qualification"
                  name="qualification"
                  value={formData.qualification}
                  onChange={handleInputChange}
                  className="focus-visible:ring-mainColor"
                />
                {errors?.identifier && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.identifier[0]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="salary">الراتب الشهري</label>
                <Input
                  id="salary"
                  name="salary"
                  type="number"
                  min="0"
                  value={formData.salary}
                  onChange={handleNumberInputChange}
                  className="focus-visible:ring-mainColor"
                />
                {errors?.salary && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.salary[0]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="role">الصلاحية</label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => handleSelectChange("role", value)}
                >
                  <SelectTrigger className="focus:ring-mainColor">
                    <SelectValue placeholder="اختر الصلاحية" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">مسؤول</SelectItem>
                    <SelectItem value="user">موظف</SelectItem>
                  </SelectContent>
                </Select>
                {errors?.role && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.role[0]}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="category_id">القسم</label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) =>
                    handleSelectChange("category_id", +value)
                  }
                >
                  <SelectTrigger className="focus:ring-mainColor">
                    <SelectValue placeholder="اختر القسم" />
                  </SelectTrigger>
                  <SelectContent>
                    {Categories?.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors?.category_id && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.category_id[0]}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <CardFooter className="flex justify-between mt-6 px-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="hover:bg-secondaryColor/10 hover:text-mainColor"
          >
            إلغاء
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className="bg-mainColor hover:bg-secondaryColor text-white"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                جاري الحفظ...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                حفظ التغييرات
              </span>
            )}
          </Button>
        </CardFooter>
      </form>
    </motion.div>
  );
}
